/**
 * MetricCard — renders a `metric` DashboardItem as a KPI tile.
 *
 *   chart_config.metric = {
 *     column,      // required when agg !== 'count'
 *     agg,         // first | sum | avg | min | max | count | count_distinct
 *     format,      // number | usd | percent | compact
 *     prefix,
 *     suffix,
 *     label,
 *     spark,       // optional column to plot as sparkline
 *     color,       // optional hex (e.g. '#1677ff') — applied to the value text
 *     icon,        // optional preset key ('auto' | 'none' | 'dollar' | 'percent' | ...)
 *   }
 */
import React, { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { Spin, Alert } from 'antd';
import { executeDashboardSQL } from '../services/dashboardService';
import { setChartData } from '../utils/chartDataCache';
import MiniSparkline from './MiniSparkline';
import Skeleton from './Skeleton';
import ErrorPanel from './ErrorPanel';
import { MetricIcon } from './metricIcons';

const computeMetric = (columns, rows, cfg) => {
  if (!cfg) return { value: null, error: 'No metric config.' };
  const agg = (cfg.agg || 'first').toLowerCase();
  if (!rows || rows.length === 0) return { value: null, error: 'No rows.' };
  if (agg === 'count') return { value: rows.length };
  const colName = cfg.column;
  if (!colName) return { value: null, error: 'No column selected.' };
  const idx = columns.indexOf(colName);
  if (idx < 0) return { value: null, error: `Column "${colName}" not in result.` };
  const raw = rows.map((r) => r[idx]);
  if (agg === 'first') return { value: raw[0] };
  if (agg === 'count_distinct') {
    const seen = new Set();
    for (const v of raw) {
      if (v === null || v === undefined || v === '') continue;
      seen.add(typeof v === 'object' ? JSON.stringify(v) : v);
    }
    return { value: seen.size };
  }
  const nums = raw.map((v) => (v === null || v === undefined || v === '' ? null : Number(v)))
    .filter((v) => v !== null && !Number.isNaN(v));
  if (!nums.length) return { value: null, error: 'No numeric values.' };
  switch (agg) {
    case 'sum': return { value: nums.reduce((a, b) => a + b, 0) };
    case 'avg': return { value: nums.reduce((a, b) => a + b, 0) / nums.length };
    case 'min': return { value: Math.min(...nums) };
    case 'max': return { value: Math.max(...nums) };
    default: return { value: raw[0] };
  }
};

const formatCompact = (n) => {
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(abs >= 1e13 ? 1 : 2) + 'T';
  if (abs >= 1e9)  return (n / 1e9 ).toFixed(abs >= 1e10 ? 1 : 2) + 'B';
  if (abs >= 1e6)  return (n / 1e6 ).toFixed(abs >= 1e7  ? 1 : 2) + 'M';
  if (abs >= 1e3)  return (n / 1e3 ).toFixed(abs >= 1e4  ? 1 : 2) + 'K';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatValue = (v, cfg) => {
  if (v === null || v === undefined) return '—';
  const fmt = cfg?.format || 'number';
  const prefix = cfg?.prefix ?? (fmt === 'usd' ? '$' : '');
  const suffix = cfg?.suffix ?? (fmt === 'percent' ? '%' : '');
  if (typeof v !== 'number' || Number.isNaN(Number(v))) return `${prefix}${v}${suffix}`;
  const num = Number(v);
  let body;
  if (fmt === 'compact') {
    body = formatCompact(num);
  } else if (fmt === 'usd') {
    // Auto-compact USD when the magnitude is large.
    body = Math.abs(num) >= 10000
      ? formatCompact(num)
      : num.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  } else if (fmt === 'percent') {
    body = num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  } else {
    body = Math.abs(num) >= 1e6
      ? formatCompact(num)
      : Math.abs(num) >= 1000
        ? num.toLocaleString('en-US', { maximumFractionDigits: 2 })
        : num.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return `${prefix}${body}${suffix}`;
};

// Pick a numeric column from the result for the sparkline series.
// Only renders when the user has explicitly chosen a sparkline column;
// auto-falling-back to the aggregation column produced misleading
// "fake" trend lines for non-time-ordered queries.
function pickSparkSeries(columns, rows, cfg) {
  if (!rows || rows.length < 2) return null;
  const colName = cfg?.spark;
  if (!colName) return null;
  const idx = columns.indexOf(colName);
  if (idx < 0) return null;
  const series = rows.map((r) => Number(r[idx])).filter((v) => !Number.isNaN(v));
  return series.length >= 2 ? series : null;
}

export default function MetricCard({ item, sqlOverride = null }) {
  const [status, setStatus] = useState('loading');
  const [err, setErr] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  const effectiveSql = sqlOverride || item?.sql_query;

  const load = React.useCallback(() => {
    if (!effectiveSql) { setStatus('error'); setErr('No SQL query.'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    // eslint-disable-next-line no-console
    console.debug('[DBG metric]', item?.id, 'fetching', effectiveSql?.slice(0, 80));
    (async () => {
      try {
        const resp = await executeDashboardSQL(effectiveSql, 5000);
        if (cancelled) return;
        const p = resp?.data || resp;
        setChartData(item.id, { columns: p.columns || [], rows: p.rows || [] });
        setColumns(p.columns || []);
        setRows(p.rows || []);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { setErr(e?.message || 'Query failed'); setStatus('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [item?.id, effectiveSql]);

  useEffect(() => { return load(); }, [load]);

  const cfg = item?.chart_config?.metric;
  const { value, error: computeErr } = useMemo(
    () => (status === 'ready' ? computeMetric(columns, rows, cfg) : { value: null }),
    [status, columns, rows, cfg],
  );
  const sparkSeries = useMemo(
    () => (status === 'ready' ? pickSparkSeries(columns, rows, cfg) : null),
    [status, columns, rows, cfg],
  );

  if (status === 'loading') return <Skeleton kind="metric" />;
  if (status === 'error') return <ErrorPanel message={err} onRetry={load} />;
  if (computeErr) {
    return <div style={{ padding: 12 }}><Alert type="warning" showIcon message={computeErr} /></div>;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100%', padding: 12, textAlign: 'center', gap: 8,
    }}>
      {cfg?.label && (
        <div style={{
          color: 'var(--theme-textSecondary, #888)', fontSize: 11, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {cfg.label}
        </div>
      )}
      <div style={{
        fontSize: 'clamp(28px, 4vw, 56px)', fontWeight: 700, lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: cfg?.color || 'var(--theme-text, #1f2937)',
        display: 'inline-flex', alignItems: 'center', gap: 12,
      }}>
        <MetricIcon cfg={cfg} style={{ fontSize: '0.7em', opacity: 0.85 }} />
        <span>{formatValue(value, cfg)}</span>
      </div>
      {sparkSeries && (
        <div style={{ width: '90%', maxWidth: 280, height: 36, marginTop: 4 }}>
          <MiniSparkline values={sparkSeries} height={36} />
        </div>
      )}
    </div>
  );
}

export { computeMetric, formatValue };
